import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";

import {
  addProjectContact,
  listContacts,
  removeProjectContact,
  updateContact,
  updateProjectContactRole,
  type Contact,
  type ContactDetailsPayload,
  type ContactKind,
  type ProjectContact,
  type ProjectContactPayload,
} from "@/api/development_suite";
import {
  CONTACT_KIND_LABEL,
  ContactKindIcon,
  contactSummary,
  roleLabel,
} from "@/components/development_suite/contactDisplay";
import {
  blankToNull,
  developmentSuiteKeys,
  formatDateTime,
  retryDevelopmentSuiteRequest,
} from "@/components/development_suite/developmentSuiteQueries";
import { RequestFailure } from "@/components/development_suite/DevelopmentSuiteStates";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useDebounce from "@/hooks/useDebounce";

type Source = "existing" | "new";

type DetailValues = { name: string; email: string; phone: string };

function detailValues(contact: Contact | null): DetailValues {
  return { name: contact?.name ?? "", email: contact?.email ?? "", phone: contact?.phone ?? "" };
}

function detailsPayload(values: DetailValues): ContactDetailsPayload {
  return { name: values.name.trim(), email: blankToNull(values.email), phone: blankToNull(values.phone) };
}

function detailsMatch(values: DetailValues, contact: Contact): boolean {
  const payload = detailsPayload(values);
  return payload.name === contact.name && payload.email === contact.email && payload.phone === contact.phone;
}

type ProjectContactDialogProps = {
  municipalityId: number;
  projectId: number;
  /** The roles the server accepts, from the contact list response. */
  roles: string[];
  /** The link to open and edit, or null to add a contact to the project. */
  link: ProjectContact | null;
  /** Where focus lands on close when the control that opened the dialog is gone. */
  fallbackFocusId: string;
  onClose: () => void;
  onSaved: (link: ProjectContact, created: boolean) => void;
  onRemoved: (link: ProjectContact) => void;
};

/**
 * Adds a contact to the project with a role, or edits one already on it.
 * Editing writes the contact's details (shared everywhere the contact is
 * used) and the role (this project only) as separate requests, and says
 * which one failed. Mount only while open, so each open starts from the
 * saved values.
 */
export function ProjectContactDialog({
  municipalityId,
  projectId,
  roles,
  link,
  fallbackFocusId,
  onClose,
  onSaved,
  onRemoved,
}: ProjectContactDialogProps) {
  // Focus returns to the control that opened the dialog, or, after a removal
  // has taken that row away, to the section, never to the document body.
  const openerRef = useRef(document.activeElement as HTMLElement | null);
  const restoreFocus = (event: Event) => {
    event.preventDefault();
    const opener = openerRef.current;
    (opener?.isConnected ? opener : document.getElementById(fallbackFocusId))?.focus();
  };

  const [source, setSource] = useState<Source>("existing");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Contact | null>(null);
  const [kind, setKind] = useState<ContactKind>("person");
  const [details, setDetails] = useState<DetailValues>(() => detailValues(link?.contact ?? null));
  const [role, setRole] = useState(link?.role ?? "");
  // What the server last confirmed, so a retry after a partial save resends
  // only what is still unsaved.
  const [savedContact, setSavedContact] = useState<Contact | null>(link?.contact ?? null);
  const [failedStep, setFailedStep] = useState<"details" | "role" | null>(null);
  const [problem, setProblem] = useState<"contact" | "name" | "role" | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // Each of these swaps unmount the control that was just used, which would
  // drop focus to the top of the dialog; move it to the natural next control.
  const keepButtonRef = useRef<HTMLButtonElement>(null);
  const removeButtonRef = useRef<HTMLButtonElement>(null);
  const changeButtonRef = useRef<HTMLButtonElement>(null);
  const hasConfirmedRef = useRef(false);
  const hasSelectedRef = useRef(false);
  useEffect(() => {
    if (confirmingRemove) {
      hasConfirmedRef.current = true;
      keepButtonRef.current?.focus();
    } else if (hasConfirmedRef.current) {
      removeButtonRef.current?.focus();
    }
  }, [confirmingRemove]);
  useEffect(() => {
    if (selected) {
      hasSelectedRef.current = true;
      changeButtonRef.current?.focus();
    } else if (hasSelectedRef.current) {
      document.getElementById("contact-search")?.focus();
    }
  }, [selected]);

  const addMutation = useMutation({
    mutationKey: developmentSuiteKeys.all(municipalityId),
    mutationFn: (payload: ProjectContactPayload) => addProjectContact(municipalityId, projectId, payload),
    onSuccess: (saved) => onSaved(saved, true),
  });

  const saveMutation = useMutation({
    mutationKey: developmentSuiteKeys.all(municipalityId),
    mutationFn: async () => {
      if (!link || !savedContact) throw new Error("Nothing to save.");
      let current = link;
      if (!detailsMatch(details, savedContact)) {
        try {
          const contact = await updateContact(municipalityId, link.contact.id, detailsPayload(details));
          current = { ...current, contact };
          setSavedContact(contact);
          // The server normalizes (lowercases email, trims), so show its copy.
          setDetails(detailValues(contact));
        } catch (error) {
          setFailedStep("details");
          throw error;
        }
      }
      if (role !== current.role) {
        try {
          current = await updateProjectContactRole(municipalityId, projectId, link.id, role);
        } catch (error) {
          setFailedStep("role");
          throw error;
        }
      }
      return current;
    },
    onSuccess: (saved) => onSaved(saved, false),
  });

  const removeMutation = useMutation({
    mutationKey: developmentSuiteKeys.all(municipalityId),
    mutationFn: () => {
      if (!link) throw new Error("Nothing to remove.");
      return removeProjectContact(municipalityId, projectId, link.id);
    },
    onSuccess: () => link && onRemoved(link),
  });

  const saving = addMutation.isPending || saveMutation.isPending || removeMutation.isPending;
  const saveError = addMutation.error ?? saveMutation.error;
  const detailsSavedThisOpen = savedContact !== link?.contact;

  // Clears only a settled error; resetting a pending save would re-enable Submit mid-write.
  const clearSaveError = () => {
    if (addMutation.isError) addMutation.reset();
    if (saveMutation.isError) saveMutation.reset();
    if (removeMutation.isError) removeMutation.reset();
    setFailedStep(null);
  };
  const edit = (change: () => void) => {
    clearSaveError();
    setProblem(null);
    change();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    clearSaveError();

    if (link) {
      if (!details.name.trim()) {
        setProblem("name");
        document.getElementById("contact-name")?.focus();
        return;
      }
      setProblem(null);
      if (savedContact && detailsMatch(details, savedContact) && role === link.role) {
        onClose();
        return;
      }
      saveMutation.mutate();
      return;
    }

    if (source === "existing" && !selected) {
      setProblem("contact");
      document.getElementById("contact-search")?.focus();
      return;
    }
    if (source === "new" && !details.name.trim()) {
      setProblem("name");
      document.getElementById("contact-name")?.focus();
      return;
    }
    if (!role) {
      setProblem("role");
      document.getElementById(`contact-role-${roles[0]}`)?.focus();
      return;
    }
    setProblem(null);
    addMutation.mutate(
      source === "existing" && selected
        ? { role, contact_id: selected.id }
        : { role, contact: { kind, ...detailsPayload(details) } },
    );
  };

  const title = link ? link.contact.name : "Add contact";

  return (
    <Dialog open onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" onCloseAutoFocus={restoreFocus}>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {link
                ? "Contact details apply everywhere this contact is used. The role applies to this project only."
                : "The people and organizations involved in this project, such as the owner or designer. Adding a contact gives them no access to Ordinal."}
            </DialogDescription>
          </DialogHeader>

          {link ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md border bg-muted/30 p-3 text-xs">
              <dt className="text-muted-foreground">Type</dt>
              <dd>{CONTACT_KIND_LABEL[link.contact.kind]}</dd>
              <dt className="text-muted-foreground">Added</dt>
              <dd>
                {formatDateTime(link.created_at)}
                {link.created_by ? ` by ${link.created_by.email}` : ""}
              </dd>
            </dl>
          ) : null}

          {saveError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{failedStep === "role" ? "The role was not saved" : "The contact was not saved"}</AlertTitle>
              <AlertDescription>
                {saveError.message}
                {failedStep === "role" && detailsSavedThisOpen ? " The contact details were saved." : ""} Your changes
                are still here, so you can correct them and try again.
              </AlertDescription>
            </Alert>
          ) : null}
          {removeMutation.error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>The contact was not removed</AlertTitle>
              <AlertDescription>{removeMutation.error.message} Try again.</AlertDescription>
            </Alert>
          ) : null}

          {/* Fields lock while saving or confirming a removal, so nothing typed is silently dropped. */}
          <fieldset disabled={saving || confirmingRemove} className="min-w-0 space-y-4">
            {link ? (
              <ContactDetailFields
                details={details}
                nameMissing={problem === "name"}
                onChange={(next) => edit(() => setDetails(next))}
              />
            ) : (
              <Tabs value={source} onValueChange={(next) => edit(() => setSource(next as Source))}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="existing">Existing contact</TabsTrigger>
                  <TabsTrigger value="new">New contact</TabsTrigger>
                </TabsList>
                <TabsContent value="existing" className="mt-4">
                  {selected ? (
                    <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <ContactKindIcon
                          kind={selected.kind}
                          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                        />
                        <div className="min-w-0">
                          <p className="break-words text-sm font-medium">{selected.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{contactSummary(selected)}</p>
                        </div>
                      </div>
                      <Button
                        ref={changeButtonRef}
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => edit(() => setSelected(null))}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <ContactPicker
                      municipalityId={municipalityId}
                      search={search}
                      contactMissing={problem === "contact"}
                      onSearchChange={(next) => edit(() => setSearch(next))}
                      onSelect={(contact) => edit(() => setSelected(contact))}
                    />
                  )}
                </TabsContent>
                <TabsContent value="new" className="mt-4">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <p id="contact-kind-label" className="text-sm font-medium leading-none">
                        Type
                      </p>
                      <RadioGroup
                        aria-labelledby="contact-kind-label"
                        value={kind}
                        onValueChange={(next) => edit(() => setKind(next as ContactKind))}
                        className="flex flex-wrap gap-x-6 gap-y-2"
                      >
                        {(Object.keys(CONTACT_KIND_LABEL) as ContactKind[]).map((option) => (
                          <div key={option} className="flex items-center gap-2">
                            <RadioGroupItem value={option} id={`contact-kind-${option}`} />
                            <Label htmlFor={`contact-kind-${option}`} className="font-normal">
                              {CONTACT_KIND_LABEL[option]}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                    <ContactDetailFields
                      details={details}
                      nameMissing={problem === "name"}
                      onChange={(next) => edit(() => setDetails(next))}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            )}

            <div className="space-y-2">
              <p id="contact-role-label" className="text-sm font-medium leading-none">
                Role on this project
              </p>
              <RadioGroup
                aria-labelledby="contact-role-label"
                aria-invalid={problem === "role"}
                aria-describedby={problem === "role" ? "contact-role-error" : undefined}
                value={role}
                onValueChange={(next) => edit(() => setRole(next))}
                className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3"
              >
                {roles.map((option) => (
                  <div key={option} className="flex items-center gap-2">
                    <RadioGroupItem value={option} id={`contact-role-${option}`} />
                    <Label htmlFor={`contact-role-${option}`} className="font-normal">
                      {roleLabel(option)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {problem === "role" ? (
                <p id="contact-role-error" className="text-sm text-destructive">
                  Choose a role.
                </p>
              ) : null}
            </div>
          </fieldset>

          {confirmingRemove && link ? (
            <DialogFooter className="items-center gap-2 sm:justify-between">
              <p id="contact-remove-prompt" role="status" className="text-sm">
                Remove {link.contact.name} from this project? Their details and other projects are not changed.
              </p>
              <div className="flex shrink-0 gap-2">
                <Button
                  ref={keepButtonRef}
                  type="button"
                  variant="outline"
                  disabled={saving}
                  onClick={() => {
                    clearSaveError();
                    setConfirmingRemove(false);
                  }}
                >
                  Keep
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={saving}
                  aria-describedby="contact-remove-prompt"
                  onClick={() => removeMutation.mutate()}
                >
                  {removeMutation.isPending ? "Removing…" : "Remove"}
                </Button>
              </div>
            </DialogFooter>
          ) : (
            <DialogFooter className="gap-2 sm:gap-0">
              {link ? (
                <Button
                  ref={removeButtonRef}
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive sm:mr-auto"
                  disabled={saving}
                  onClick={() => {
                    clearSaveError();
                    setConfirmingRemove(true);
                  }}
                >
                  Remove from project
                </Button>
              ) : null}
              <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving…" : link ? "Save changes" : "Add to project"}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContactDetailFields({
  details,
  nameMissing,
  onChange,
}: {
  details: DetailValues;
  nameMissing: boolean;
  onChange: (next: DetailValues) => void;
}) {
  const setField = (field: keyof DetailValues) => (value: string) => onChange({ ...details, [field]: value });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="contact-name">Name</Label>
        <Input
          id="contact-name"
          value={details.name}
          onChange={(event) => setField("name")(event.target.value)}
          placeholder="e.g. Pat Rivera or Rivera Architects"
          maxLength={255}
          aria-invalid={nameMissing}
          aria-describedby={nameMissing ? "contact-name-error" : undefined}
        />
        {nameMissing ? (
          <p id="contact-name-error" className="text-sm text-destructive">
            Enter a name.
          </p>
        ) : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="contact-email">Email</Label>
          <Input
            id="contact-email"
            type="email"
            value={details.email}
            onChange={(event) => setField("email")(event.target.value)}
            placeholder="Optional"
            maxLength={255}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact-phone">Phone</Label>
          <Input
            id="contact-phone"
            type="tel"
            value={details.phone}
            onChange={(event) => setField("phone")(event.target.value)}
            placeholder="Optional"
            maxLength={50}
          />
        </div>
      </div>
    </div>
  );
}

/** Searches the municipality's contacts by name or email and offers each match. */
function ContactPicker({
  municipalityId,
  search,
  contactMissing,
  onSearchChange,
  onSelect,
}: {
  municipalityId: number;
  search: string;
  contactMissing: boolean;
  onSearchChange: (next: string) => void;
  onSelect: (contact: Contact) => void;
}) {
  const term = useDebounce(search.trim(), 250);
  const query = useQuery({
    queryKey: developmentSuiteKeys.contactSearch(municipalityId, term),
    queryFn: () => listContacts(municipalityId, term),
    retry: retryDevelopmentSuiteRequest,
    placeholderData: (previous) => previous,
  });
  const contacts = query.isError ? [] : (query.data?.contacts ?? []);
  const pagination = query.isError ? undefined : query.data?.pagination;
  // Results shown for an older term are not this term's answer yet.
  const stale = search.trim() !== term || query.isPlaceholderData;

  // Enter would submit the form with nothing chosen; take the single match instead.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (!stale && contacts.length === 1) onSelect(contacts[0]);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="contact-search">Search contacts</Label>
      <Input
        id="contact-search"
        type="search"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Name or email"
        autoComplete="off"
        aria-invalid={contactMissing}
        aria-describedby={contactMissing ? "contact-search-error" : "contact-search-hint"}
      />
      {contactMissing ? (
        <p id="contact-search-error" className="text-sm text-destructive">
          Choose a contact from the list, or switch to New contact.
        </p>
      ) : (
        <p id="contact-search-hint" className="sr-only">
          Matching contacts appear below as you type.
        </p>
      )}

      {query.isLoading ? (
        <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading contacts…
        </p>
      ) : query.isError ? (
        <RequestFailure error={query.error} what="Contacts" onRetry={() => void query.refetch()} />
      ) : (
        <>
          <p aria-live="polite" className="text-xs text-muted-foreground">
            {stale
              ? "Searching…"
              : pagination && pagination.total_pages > 1
                ? `Showing the first ${pagination.per_page} of ${pagination.total_count} matches. Keep typing to narrow the list.`
                : `${contacts.length} ${contacts.length === 1 ? "match" : "matches"}`}
          </p>
          {contacts.length === 0 ? (
            stale ? null : (
              <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
                {term ? `No contacts match “${term}”.` : "No contacts in this municipality yet."} Switch to New contact
                to add one.
              </p>
            )
          ) : (
            <ul aria-label="Matching contacts" className="max-h-56 divide-y overflow-y-auto rounded-md border">
              {contacts.map((contact) => (
                <li key={contact.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(contact)}
                    className="flex w-full items-start gap-3 px-3 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                  >
                    <ContactKindIcon kind={contact.kind} className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0">
                      <span className="block break-words font-medium">{contact.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">{contactSummary(contact)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
